import clsx from 'clsx';

import { getRelativeTime } from '../utils/utils';
import { BookOpenCheck } from 'lucide-react';
import { Notification, NotificationTypes } from '../../helpers/utils/types';

import { useModal } from '../hooks/modalContext';

import { useNotificationListener } from '../hooks/NotificationContext';
interface NotificationDropdownProps {
  isOpen: boolean;
  onClose?: () => void;
  notifications?: Notification[];
  containerRef: React.RefObject<HTMLDivElement>; // new
}

const isToday = (date: Date) => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

const isYesterday = (date: Date) => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  );
};

const NotificationDropdown = ({
  isOpen,

  notifications,
  containerRef,
}: NotificationDropdownProps) => {
  const { markNotificationAsRead, fetchMoreNotifications, hasMore } = useNotificationListener();
  const { setModal } = useModal();

  if (!isOpen) return null;

  const openNotification = (
    notificationId: string,
    referenceId: string,
    notificationTypeName: NotificationTypes
  ) => {
    markNotificationAsRead(notificationId);
    switch (notificationTypeName) {
      case NotificationTypes.PaymentSuccess:
        setModal('past-transactions', {
          tenantTransactionId: referenceId,
        });
        break;
      case NotificationTypes.PaymentFailed:
        setModal('past-transactions', {
          tenantTransactionId: referenceId,
        });
        break;
      case NotificationTypes.MitSubscriptionSuccess:
        setModal('subscription-detail', {
          tenantMitSubscriptionId: referenceId,
        });
        break;
      case NotificationTypes.MitSubscriptionFailed:
        setModal('subscription-detail', {
          tenantBuildingId: referenceId,
        });
        break;
      case NotificationTypes.MitPaymentFailed:
        setModal('subscription-detail', {
          tenantBuildingId: referenceId,
        });
        break;
      case NotificationTypes.MitPaymentSuccess:
        setModal('subscription-detail', {
          tenantMitSubscriptionId: referenceId,
        });
        break;
      case NotificationTypes.PointEarned:
        setModal('rewards');
        break;
      default:
        break;
    }
  };

  const groupedNotifications: Record<string, Notification[]> = {
    Today: [],
    Yesterday: [],
    Earlier: [],
  };

  // Group notifications by date
  notifications?.forEach((notification) => {
    const date = new Date(notification.timestampAdded);
    if (isToday(date)) {
      groupedNotifications.Today.push(notification);
    } else if (isYesterday(date)) {
      groupedNotifications.Yesterday.push(notification);
    } else {
      groupedNotifications.Earlier.push(notification);
    }
  });

  return (
    <div
      ref={containerRef}
      className={clsx(
        'max-h-[28rem] overflow-y-auto rounded-[12px] bg-white/90 shadow-[0_12px_50px_0_rgba(166,177,203,0.6)] z-50 notification-scrollbar backdrop-blur-[15px] fixed top-[62px] left-3 right-3 w-auto',
        'sm:absolute sm:top-12 sm:inset-auto sm:right-2 sm:w-[370px]'
      )}
      style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }}
    >
      {/* Header */}
      {/* <div className='p-4 flex justify-end'>
          {(notifications?.length ?? 0) > 0 && (
            <button onClick={markAllAsRead} className='text-sm text-primary hover:underline'>
              Mark all as read
            </button>
          )}
        </div> */}

      {/* Notification List */}
      <div className='flex flex-col items-start px-4 py-6 sm:px-8 sm:py-8'>
        {notifications?.length ? (
          <>
            {['Today', 'Yesterday', 'Earlier'].map((section) => {
              const sectionNotifications = groupedNotifications[section];
              if (!sectionNotifications.length) return null;

              return (
                <div key={section}>
                  {/* Section Header */}
                  <div className='px-4 py-2 text-sm font-bold text-primary uppercase tracking-wide '>
                    {section}
                  </div>

                  {/* Notifications in this section */}
                  {sectionNotifications.map((notification) => {
                    const isUnread = !notification.timestampViewed;

                    return (
                      <div
                        key={notification.notificationId}
                        onClick={() =>
                          openNotification(
                            notification.notificationId,
                            notification.referenceId,
                            notification.notificationTypeName
                          )
                        }
                        className={clsx(
                          'relative group cursor-pointer transition-all duration-200 ease-in-out rounded-md',
                          'px-4 py-3 my-2',
                          isUnread
                            ? 'bg-blue-50 border-l-4 border-blue-500 shadow-sm'
                            : ' hover:bg-gray-50',
                          'hover:scale-[1.01] active:scale-100'
                        )}
                      >
                        <div className='flex items-start gap-3'>
                          <div className='flex-1'>
                            <div
                              className={clsx(
                                'text-sm leading-snug font-medium text-main',
                                isUnread ? 'text-blue-900' : 'text-gray-800'
                              )}
                            >
                              {notification.message}
                            </div>
                            <div className='mt-1 text-xs text-gray-500 font-medium'>
                              {getRelativeTime(notification.timestampAdded)}
                            </div>
                          </div>
                        </div>

                        {isUnread && (
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              markNotificationAsRead(notification.notificationId);
                            }}
                            className='group/icon absolute top-1/2 right-4 -translate-y-1/2 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-300 ease-in-out bg-white p-2 rounded-full shadow hover:bg-gray-100'
                          >
                            <BookOpenCheck className='w-4 h-4 text-green-600' />
                            <span className='absolute -left-12 top-full mt-1 px-2 py-1 text-xs bg-gray-800 text-white rounded opacity-0 group-hover/icon:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10'>
                              Mark as read
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {/* Show More Button */}
            {hasMore && (
              <div className='p-4 flex justify-center'>
                <button
                  onClick={fetchMoreNotifications}
                  className='text-sm text-primary hover:underline cursor-pointer'
                >
                  Show more notifications
                </button>
              </div>
            )}
          </>
        ) : (
          <div className='p-4 text-sm text-gray-500 text-center'>No notifications.</div>
        )}
      </div>
    </div>
  );
};

export default NotificationDropdown;
